/**
 * useLmsMe: the caller's Learning Center identity for the current site
 * (GET /api/lms/me, Appendix A), read from LmsProvider.
 *
 * Calling the hook is what loads it: the provider fetches nothing on its own
 * (it wraps the whole app), so the first consumer inside the module shell
 * triggers one request per site and every later consumer shares the answer.
 * A failed load stays failed (no retry loop) until refresh() is called.
 *
 * isLmsAdmin is false until a successful answer says otherwise: the admin tabs
 * stay hidden while loading and after a failure (plan section 13; the server
 * enforces requireLmsAdmin on every admin route regardless).
 */
import { useEffect } from 'react';
import { useLms } from '../LmsProvider';
import type { LmsMe } from '../api/client';

export interface UseLmsMeResult {
  me: LmsMe | null;
  /** True until the current site's answer (or failure) is in. */
  loading: boolean;
  error: string | null;
  isLmsAdmin: boolean;
  refresh: () => void;
}

export function useLmsMe(): UseLmsMeResult {
  const { siteId, me, meStatus, meError, loadMe, refreshMe } = useLms();

  useEffect(() => {
    if (siteId) loadMe();
  }, [siteId, loadMe]);

  return {
    me,
    loading: siteId !== null && (meStatus === 'idle' || meStatus === 'loading'),
    error: meStatus === 'error' ? meError : null,
    isLmsAdmin: meStatus === 'ready' && me?.isLmsAdmin === true,
    refresh: refreshMe,
  };
}

export default useLmsMe;
